import {
  addDays as addDaysFn,
  endOfDay as endOfDayFn,
  isAfter as isAfterFn,
  isBefore as isBeforeFn,
  isValid,
  isWithinInterval,
  parseISO,
  startOfDay as startOfDayFn,
} from 'date-fns';

/** Values the date helpers accept from APIs, form fields, or timestamps. */
export type DateInput = Date | string | number | null | undefined;

/**
 * Coerce a Date, ISO string, or timestamp to a valid Date. Returns null when the
 * value is empty or not a real date (so callers never get Invalid Date).
 */
export const toDate = (value: DateInput): Date | null => {
  if (value == null) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    const date = new Date(value);
    return isValid(date) ? date : null;
  }
  const trimmed = value.trim();
  if (!trimmed) return null;
  const date = parseISO(trimmed);
  return isValid(date) ? date : null;
};

export const isValidDate = (value: DateInput): boolean => toDate(value) !== null;

export const now = (): Date => new Date();

/** Calendar date of now, at local midnight. */
export const today = (): Date => startOfDayFn(now());

export const startOfDay = (value: DateInput): Date | null => {
  const date = toDate(value);
  return date ? startOfDayFn(date) : null;
};

export const endOfDay = (value: DateInput): Date | null => {
  const date = toDate(value);
  return date ? endOfDayFn(date) : null;
};

export const addDays = (value: DateInput, amount: number): Date | null => {
  const date = toDate(value);
  return date ? addDaysFn(date, amount) : null;
};

export const isBefore = (value: DateInput, compareTo: DateInput): boolean => {
  const left = toDate(value);
  const right = toDate(compareTo);
  return left != null && right != null && isBeforeFn(left, right);
};

export const isAfter = (value: DateInput, compareTo: DateInput): boolean => {
  const left = toDate(value);
  const right = toDate(compareTo);
  return left != null && right != null && isAfterFn(left, right);
};

/**
 * Inclusive by default (matches date-fns `isWithinInterval`). When `inclusive` is
 * false, the endpoints themselves are excluded. Invalid or inverted ranges are false.
 */
export const isBetween = (
  value: DateInput,
  start: DateInput,
  end: DateInput,
  inclusive = true,
): boolean => {
  const date = toDate(value);
  const from = toDate(start);
  const to = toDate(end);
  if (!date || !from || !to || from.getTime() > to.getTime()) return false;
  if (inclusive) return isWithinInterval(date, { start: from, end: to });
  return isAfterFn(date, from) && isBeforeFn(date, to);
};
